import * as React from "react";
import { IndexModel, ProjectDto } from "src/App/api/APIClient";
import { useHistory } from "react-router-dom";
import { usePlanningContext } from "src/App/Context/PlanningProvider";
import { getPrototypeView } from "src/App/api/Fetchers";
import { GetTaskStatusLegend } from "src/App/NewModels/Planning/External/Models";
import { enqueueSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { PanelAppContext } from "../../../PanelAppProvider";
import CookiesModel from "../../../CookiesModel";
import { BDTRoutes } from "../../../helpers/applicationConstants";
import PanelProjectContext, { UserRole } from "../../PanelProjectProvider";
import useAsset from "../../Project/Viewer/hooks/useAsset";
import {
    GetPrototypeViewInput,
    PrototypeViewPanels,
    RenderItem,
} from "../../Project/Settings/ViewsControl/Models";
import { ViewerMode } from "../../Project/Viewer/ForgeViewer/BIMDigitalTwinViewer/Utils";


interface IPanelProjectContext {
    isViewerReady: boolean;
    setView: React.Dispatch<React.SetStateAction<string>>;
    setPrototypeViewRightPanelData: React.Dispatch<
        React.SetStateAction<PrototypeViewPanels | null>
    >;
    setIsPVRightPanelButtonsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setIsPrototypeViewRightPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    project: ProjectDto;
    userRole: UserRole;
    setPrototypeViewRenderData: React.Dispatch<React.SetStateAction<RenderItem[] | undefined>>;
    prototypeViewLegends: GetTaskStatusLegend[] | undefined;
    setPrototypeViewLegends: React.Dispatch<
        React.SetStateAction<GetTaskStatusLegend[] | undefined>
    >;
    userId: string;
    setViewerMode: React.Dispatch<React.SetStateAction<ViewerMode>>;
    setAssetSelection: React.Dispatch<React.SetStateAction<IndexModel | undefined>>;
}

interface IPanelAppProvider {
    cookies: CookiesModel | undefined;
    setCookies: React.Dispatch<React.SetStateAction<CookiesModel | undefined>>;
}

const Redirect = () => {
    const history = useHistory();
    const { t } = useTranslation("project");
    const {
        isViewerReady,
        setView,
        setPrototypeViewRightPanelData,
        setIsPVRightPanelButtonsLoading,
        setIsPrototypeViewRightPanelOpen,
        project,
        userRole,
        setPrototypeViewRenderData,
        prototypeViewLegends,
        setPrototypeViewLegends,
        userId,
        setViewerMode,
        setAssetSelection
    } = React.useContext<IPanelProjectContext>(PanelProjectContext);
    const { cookies, setCookies } = React.useContext<IPanelAppProvider>(PanelAppContext);

    const { fetchingAssetInformationById } = useAsset();

    React.useEffect(() => {
        if (cookies) {
            switch (isViewerReady) {
                case false:
                    history.push(BDTRoutes.Project.Viewer.Viewer.Builder(`${cookies.projectId}`));
                    break;
                case true:
                    switch (cookies.viewPanel) {
                        case "asset":
                            fetchingAssetInformationById(
                                `${cookies.projectId}`,
                                `${cookies.assetId}`,
                                true,
                                undefined
                            );
                            setView(`${cookies.viewButton}`);
                            break;
                        case "view":
                            window.NOP_VIEWER.hideDisks();
                            window.NOP_VIEWER.deleteElementsSpecularColor();
                            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                            // @ts-ignore
                            window.NOP_VIEWER.utilities.goHome();

                            setView("");
                            setPrototypeViewRightPanelData({});
                            setIsPVRightPanelButtonsLoading(true);
                            setIsPrototypeViewRightPanelOpen(true);
                            getPrototypeView({
                                viewId: cookies.viewId as string,
                                projectId: project.id,
                                userId,
                                byURL: true,
                            })
                                .then((res) => {
                                    const {
                                        render,
                                        legends,
                                        prototypePanelInfo,
                                        prototypePanelLogs,
                                        code,
                                    } = res.data;

                                    // La API deniega con un 200 y un 'code' porque el
                                    // proxy C# convierte cualquier 4xx en un 'NOT FOUND'
                                    // pelado. El return corta aqui: sin el se seguirian
                                    // ejecutando el hideToolbar y el render vacio sobre
                                    // el modelo.
                                    if (code) {
                                        enqueueSnackbar(
                                            t(
                                                code === "VIEW_NOT_SHARED"
                                                    ? "prototypeViews.viewNotShared"
                                                    : "prototypeViews.viewNotFound"
                                            ),
                                            {
                                                variant: "error",
                                                anchorOrigin: {
                                                    horizontal: "center",
                                                    vertical: "bottom",
                                                },
                                            }
                                        );
                                        setPrototypeViewRightPanelData({});
                                        setIsPrototypeViewRightPanelOpen(false);
                                        return;
                                    }

                                    window.NOP_VIEWER.viewingMode.backgroundColor = [];
                                    window.NOP_VIEWER.viewingMode.backgroundOpacity = 0.2;
                                    window.NOP_VIEWER.setOpacityModel(
                                        window.NOP_VIEWER.viewingMode.backgroundOpacity,
                                        window.NOP_VIEWER.viewingMode.backgroundColor
                                    );
                                    window.NOP_VIEWER.setLightPreset(6);
                                    window.NOP_VIEWER.viewingMode.light = 6;
                                    window.NOP_VIEWER.setEnvMapBackground(false);
                                    window.NOP_VIEWER.isolated = false;
                                    window.NOP_VIEWER.hideToolbar();
                                    window.NOP_VIEWER.mode = ViewerMode.SharedByURLPrototypeViews;
                                    setViewerMode(window.NOP_VIEWER.mode);
                                    setAssetSelection({});
                                    setIsPrototypeViewRightPanelOpen(true);
                                    setPrototypeViewRightPanelData({
                                        prototypePanelInfo, prototypePanelLogs});
                                    setPrototypeViewRenderData(render);
                                    setPrototypeViewLegends(legends);
                                })
                                .catch((err) => {
                                    setPrototypeViewRightPanelData({});
                                    setIsPrototypeViewRightPanelOpen(false);
                                })
                                .finally(() => {
                                    setIsPVRightPanelButtonsLoading(false);
                                });
                            break;
                        default:
                            break;
                    }

                    // Generar dinámicamente la eliminación de cookies
                    Object.keys(cookies).forEach((key) => {
                        document.cookie = `${key}=;expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
                    });

                    setCookies(undefined);

                    break;
                default:
                    break;
            }
        }
    }, [isViewerReady, cookies]);

    return <></>;
};

export default Redirect;
